import { TaskProgress } from "@/types/LCAoM/TaskProgress";
import { SelectionMode } from "@/types/SelectionManager";
import { FeatureManagementType, Model, NonModelledElementsStatus, Permissions, RebarEstimationStatus, Scenario, ScenarioModel } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useScenarios } from "@api/scenarios";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import { MultiModelDataTest } from "@components/MultiModelDataTest";
import VersionChip from "@components/VersionChip/VersionChip";
import { useAssignMaterials } from "@features/analysis/hooks/useAssignMaterials";
import { useAssignNonModelledElement } from "@features/analysis/hooks/useAssignNonModelledElement";
import { useAssignRebarEstimations } from "@features/analysis/hooks/useAssignRebarEstimations";
import { useAutoAssignMaterials } from "@features/analysis/hooks/useAutoAssignMaterials";
import { useModelsData } from "@features/analysis/hooks/useModelsData";
import { invalidateProjectAnalysisScenarioNodeData } from "@features/analysis/hooks/useAllScenariosProjectNodeData";
import { useRemoveMaterials } from "@features/analysis/hooks/useRemoveMaterials";
import { setSelectedNodesKeys } from "@features/analysis/utils/treeGridUtils";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import { invalidateModelCache } from "@/types/storage/modelDataStorage";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BugReportIcon from "@mui/icons-material/BugReport";
import CompressIcon from "@mui/icons-material/Compress";
import MemoryIcon from "@mui/icons-material/Memory";
import { Button, Checkbox, CircularProgress, Grid, ListItemText, ListSubheader, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MuiAssignGrid, { AssignGridHandle } from "./MuiAssignGrid";

export interface MuiAssignTabProps {
  /** When provided (e.g. from InventoryPane), shared with Results tab for useProjectAnalysisData */
  assignGridApiRef?: MutableRefObject<GridApiPremium>;
}

const MuiAssignTab = observer((props: MuiAssignTabProps) => {
  const { assignGridApiRef: assignGridApiRefProp } = props;
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const upStore = useUserPermissionStore();
  const queryClient = useQueryClient();
  const internalGridApiRef = useGridApiRef();
  const assignGridApiRef = assignGridApiRefProp ?? internalGridApiRef;
  const [openAssignMaterialDlg, setOpenAssignMaterialDlg] = useState(false);
  const [rebarEstimationStatus, setRebarEstimationStatus] = useState<RebarEstimationStatus | undefined>();
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [enableTreeData, setEnableTreeData] = useState(true);
  const [useMainThread, setUseMainThread] = useState(true);
  const [taskProgress, setTaskProgress] = useState<TaskProgress | null>(null);
  const [hasGridSelection, setHasGridSelection] = useState(false);
  const scenarios = useScenarios(projectId!);
  const assignMaterialDlgRef = useRef<any>();
  const assignGridRef = useRef<AssignGridHandle>(null);
  const appSettings = useAppSettings();

  const getSelectedNodes = useCallback(() => {
    setSelectedNodesKeys(assignGridApiRef, appStore);
  }, [assignGridApiRef, appStore]);

  const clearSelectedNodes = useCallback(async () => {
    console.log("[MuiAssignTab] Clearing selected nodes");
    assignGridApiRef.current.setRowSelectionModel([]);
  }, [assignGridApiRef]);

  const scenarioId = selectionManager.selectedScenarioId;
  const selectedModelIds = selectionManager.getSelectedModelIds(scenarioId);

  // Centralized data management hook - handles services, data fetching, hydration, and IDB sync
  const { modelsDataService, lcaoMService, co2MaterialTypeLookup, setServiceVersion, updateIndexedDBMaterials, multiScenarioNodeData, addToCo2MaterialTypeLookup, isCo2MaterialTypesLoading, hasAssignedMaterialTypes, isDiscoveringAssignedMaterialTypes } = useModelsData({
    assignGridApiRef,
    selectedModelIds,
    scenarioId,
    queryClient,
  });

  // Track fetching state to detect when a refetch completes and trigger tree rebuild
  const wasFetchingRef = useRef(multiScenarioNodeData.isFetching);

  // Trigger tree rebuild only when a refetch completes (isFetching transitions from true to false)
  // This avoids spurious rebuilds from reference changes on re-renders
  useEffect(() => {
    const wasFetching = wasFetchingRef.current;
    const isFetching = multiScenarioNodeData.isFetching;
    wasFetchingRef.current = isFetching;

    // Only rebuild when fetching completes (was fetching, now not fetching)
    if (wasFetching && !isFetching && multiScenarioNodeData.isSuccess) {
      console.log("[MuiAssignTab] refetch completed, triggering tree rebuild");
      assignGridRef.current?.rebuildTree();
    }
  }, [multiScenarioNodeData.isFetching, multiScenarioNodeData.isSuccess]);

  const invalidateResultsScenarioNodeData = useCallback(async () => {
    if (!projectId || !scenarioId) return;
    await invalidateProjectAnalysisScenarioNodeData(queryClient, projectId, scenarioId);
  }, [queryClient, projectId, scenarioId]);

  const refetchScenariosAndResultsMaterials = useCallback(async () => {
    if (projectId) {
      await queryClient.refetchQueries({
        queryKey: ["scenarios", projectId],
        exact: true,
      });
    }
    await invalidateResultsScenarioNodeData();
  }, [invalidateResultsScenarioNodeData, projectId, queryClient]);

  const { handleAssignMaterials, isAssignMaterialsPending, isUpsertNodeMaterialsPending } = useAssignMaterials({
    assignMaterialDlgRef,
    addToCo2MaterialTypeLookup,
    lcaoMService,
    clearSelectedNodes,
    onSuccess: refetchScenariosAndResultsMaterials,
    setOpenAssignMaterialDlg,
    setServiceVersion,
    setTaskProgress,
    updateIndexedDBMaterials,
  });

  const { handleAutoAssignMaterials, isAutoAssignMaterialsPending } = useAutoAssignMaterials({
    clearSelectedNodes,
    assignGridApiRef,
    onSuccess: async () => {
      await refetchScenariosAndResultsMaterials();
      await multiScenarioNodeData.refetchAll();
    },
  });

  const { handleAssignRebarEstimations, isAssignRebarEstimationsPending } = useAssignRebarEstimations({
    onSuccess: async () => {
      await multiScenarioNodeData.refetchAll();
      await invalidateResultsScenarioNodeData();
    },
  });

  const { handleAssignNonModelledElements, isAssignNonModelledElementsPending } = useAssignNonModelledElement({
    onSuccess: async () => {
      await multiScenarioNodeData.refetchAll();
      await invalidateResultsScenarioNodeData();
    },
  });

  const refetchAll = useCallback(async () => {
    await multiScenarioNodeData.refetchAll();
  }, [multiScenarioNodeData]);

  const clearAllCaches = useCallback(async () => {
    if (!scenarioId || selectedModelIds.length === 0) return;
    await Promise.all(selectedModelIds.map((modelId) => invalidateModelCache(modelId, scenarioId)));
    await Promise.all(
      selectedModelIds.map((modelId) =>
        queryClient.removeQueries({
          queryKey: ["scenarioNodeData", modelId, scenarioId],
          exact: true,
        })
      )
    );
    await Promise.all(
      selectedModelIds.map((modelId) =>
        queryClient.removeQueries({
          queryKey: ["scenarioNodes", modelId, scenarioId],
          exact: true,
        })
      )
    );
    await Promise.all(
      selectedModelIds.map((modelId) =>
        queryClient.removeQueries({
          queryKey: ["scenarioNodeMaterials", modelId, scenarioId],
          exact: true,
        })
      )
    );
    await Promise.all(
      selectedModelIds.map((modelId) =>
        queryClient.removeQueries({
          queryKey: ["scenarioNodeSupplementals", modelId, scenarioId],
          exact: true,
        })
      )
    );
    console.info("[MuiAssignTab] Selected model caches cleared");
  }, [queryClient, scenarioId, selectedModelIds]);

  const { handleRemoveMaterials, isRemoveMaterialsPending, isRemoveSupplementalsPending } = useRemoveMaterials({
    clearSelectedNodes,
    assignGridApiRef,
    onSuccess: async () => {
      await refetchScenariosAndResultsMaterials();
      await refetchAll();
    },
  });

  // Buttons are disabled only during active mutations — not while node materials or
  // CO₂ emission-factor display data loads in the background. Grid cells show "" for
  // material name/unit until the scoped by-ids fetch resolves.
  const isMutating =
    isAssignMaterialsPending ||
    isAutoAssignMaterialsPending ||
    isAssignRebarEstimationsPending ||
    isAssignNonModelledElementsPending ||
    isRemoveMaterialsPending ||
    isRemoveSupplementalsPending ||
    isUpsertNodeMaterialsPending;
  const isBusy = isMutating;
  const hasModelSelection = !!(selectionManager.selectedScenario && selectionManager.selectedModel);

  const handleScenarioChange = (event: any) => {
    const scenario = scenarios.data?.find((s) => s.scenarioId === event.target.value);
    if (scenario && scenario !== selectionManager.selectedScenario) {
      // Defer heavy processing to allow UI to update (dropdown closes, selection renders)
      requestAnimationFrame(() => {
        setTimeout(async () => {
          await selectionManager.selectScenario(scenario.scenarioId!);
        }, 0);
      });
    }
  };

  const selectModel = async (model: Model | undefined) => {
    if (model?.modelId && selectionManager.selectedScenarioId) {
      await selectionManager.selectModels(selectionManager.selectedScenarioId, model.modelId);
    }
  };

  const handleModelChange = async (event: any) => {
    const newModelIds = event.target.value as string[];
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId) return;

    const previousModelIds = selectionManager.getSelectedModelIds(scenarioId);
    if (previousModelIds.length === newModelIds.length && previousModelIds.every((id: string, i: number) => id === newModelIds[i])) {
      return;
    }

    const added = newModelIds.filter((id: string) => !previousModelIds.includes(id));
    const removed = previousModelIds.filter((id: string) => !newModelIds.includes(id));

    // Single unselect: use toggleModelCheckbox so tree is cleared when last model is unchecked
    if (removed.length === 1 && added.length === 0) {
      await toggleModelCheckbox(removed[0]!, false);
      return;
    }

    // Replace selection with exactly newModelIds so onLoad/download runs for the chosen set only (no merge)
    if (previousModelIds.length > 0) {
      await selectionManager.unSelectModels(scenarioId, previousModelIds);
    }
    if (newModelIds.length > 0) {
      await selectionManager.selectModels(scenarioId, newModelIds);
    } else {
      appStore.sceneManager.setTreeRows([]);
    }
  };

  const toggleModelCheckbox = async (modelId: string | undefined, checked: boolean) => {
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId || !modelId) return;
    const model = selectionManager.selectedProject?.models?.find((m: Model) => m.modelId === modelId);
    if (checked) {
      if (model) {
        await selectModel(model);
      } else {
        await selectionManager.selectModels(scenarioId, modelId);
      }
      return;
    }

    await selectionManager.unSelectModels(scenarioId, modelId);

    const selectedIds = selectionManager.getSelectedModelIds(scenarioId);
    if (selectedIds.length === 0) {
      appStore.sceneManager.setTreeRows([]);
    }
  };

  const toggleSelectAllModels = async (checked: boolean) => {
    const scenarioId = selectionManager.selectedScenarioId;
    if (!scenarioId) return;
    const allModelIds = selectionManager.getScenarioModelIds(scenarioId);
    if (allModelIds.length === 0) return;

    if (checked) {
      const selectedIds = selectionManager.getSelectedModelIds(scenarioId);
      const toAdd = allModelIds.filter((id: string) => !selectedIds.includes(id));
      if (toAdd.length > 0) {
        await selectionManager.selectModels(scenarioId, toAdd);
      }
    } else {
      const selectedIds = selectionManager.getSelectedModelIds(scenarioId).filter((id) => allModelIds.includes(id));
      if (selectedIds.length > 0) {
        await selectionManager.unSelectModels(scenarioId, selectedIds);
      }
      appStore.sceneManager.setTreeRows([]);
    }
  };

  const handleAssignMaterialDlg = async () => {
    getSelectedNodes();
    setOpenAssignMaterialDlg(true);
  };

  useEffect(() => {
    const go = async () => {
      await selectionManager.setSelectionMode(SelectionMode.SingleScenarioMultiModel);
      // Clear selection on entering the tab so nothing auto-loads;
      // user must explicitly pick scenario/model again.
      await selectionManager.unSelectAllModels(selectionManager.selectedScenarioId || "", true);
      appStore.sceneManager.setTreeRows([]);
    };
    go();
  }, []);

  // Auto-select scenario if there's only one available
  useEffect(() => {
    if (scenarios.data?.length === 1 && !selectionManager.selectedScenario) {
      const scenario = scenarios.data[0];
      if (scenario.scenarioId) {
        selectionManager.selectScenario(scenario.scenarioId);
      }
    }
  }, [scenarios.data, selectionManager.selectedScenario]);

  // Also clear selection when leaving the Assign tab (component unmount).
  // This prevents hidden/stale URNs from triggering downloads/tree-build
  // when the user re-enters the tab with no visible selections.
  useEffect(() => {
    return () => {
      try {
        selectionManager.clearSelection();
        appStore.sceneManager.setTreeRows([]);
      } catch {
        /* ignore */
      }
    };
  }, []);

  // If scenario becomes cleared (e.g., when navigating back), ensure model and any
  // lingering model selections are also cleared to keep the Models Select empty.
  useEffect(() => {
    if (!selectionManager.selectedScenario) {
      selectionManager.setSelectedModel(null);
      try {
        (selectionManager as any)?.unSelectAllScenarios?.(true);
      } catch {
        /* noop */
      }
      appStore.sceneManager.setTreeRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionManager.selectedScenario]);

  useEffect(() => {
    console.log("[MuiAssignTab] Updating rebar estimation status due to model change");
    if (selectionManager.selectedModel && selectionManager.selectedScenario) {
      const scenarioModel = selectionManager.selectedScenario.scenarioModels?.find((sm: ScenarioModel) => sm.modelId === selectionManager.selectedModel?.modelId);
      setRebarEstimationStatus(scenarioModel?.rebarEstimationStatus);
    }
  }, [selectionManager.selectedModel]);

  // Tree building for multi-model selection is handled centrally in AppStore.onDownloadsDrained

  // Selection state for model checkboxes and header "Select All"
  const allSelected = selectionManager.isAllModelsSelected(scenarioId);
  const someSelected = selectionManager.isSomeModelsSelected(scenarioId);

  return (
    <Box mt={1} pr={1} sx={{ position: "relative", display: "flex", flexDirection: "column", width: "100%", maxWidth: "100vw", minWidth: "100%", minHeight: "100%" }}>
      <Box sx={{ float: "left", position: "absolute", top: -32, right: 56 }}>
        {rebarEstimationStatus === RebarEstimationStatus.Enabled && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-enabled")}</Typography>}
        {rebarEstimationStatus === RebarEstimationStatus.Estimated && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-status")}</Typography>}
      </Box>
      <Stack direction={"row"} spacing={2}>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={4}>
            <FormControl fullWidth>
              <InputLabel id="scenario-label">{i18n.t("assign-tab.scenarios-select.title")}</InputLabel>
              <Select
                labelId="scenario-label"
                id="senario-id"
                value={selectionManager.selectedScenarioId || ""}
                label={i18n.t("assign-tab.scenarios-select.title")}
                onChange={handleScenarioChange}
                endAdornment={scenarios.isLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                renderValue={() => {
                  return <Typography>{selectionManager.selectedScenario?.name}</Typography>;
                }}
              >
                <ListSubheader>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>{i18n.t("assign-tab.scenarios-list.title")}</Box>
                    <Typography color="text.secondary">{i18n.t("assign-tab.scenarios-list.content")}</Typography>
                  </Box>
                </ListSubheader>
                {scenarios.data?.map((scenario: Scenario) => (
                  <MenuItem key={scenario.scenarioId} value={scenario.scenarioId}>
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                      }}
                    >
                      <Box>{scenario.name}</Box>
                      <Box color="text.secondary">{i18n.t("percent", { value: scenario.getPercentAssigned() })}</Box>
                    </ListItemText>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={8}>
            <FormControl fullWidth>
              <InputLabel id="model-label">{i18n.t("assign-tab.models-select.title")}</InputLabel>
              <Select
                labelId="model-label"
                id="model-id"
                value={selectionManager.getSelectedModelIds()}
                label={i18n.t("assign-tab.models-select.title")}
                disabled={!selectionManager.selectedScenario}
                onChange={handleModelChange}
                multiple
                renderValue={() => {
                  const selectedScenarioModels = selectionManager.getSelectedScenarioModels(scenarioId);
                  const firstSm = selectedScenarioModels[0];
                  const extra = Math.max(0, selectedScenarioModels.length - 1);

                  if (firstSm) {
                    const scenarioModel = firstSm;
                    return (
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid size="grow">
                          <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {firstSm.model?.modelName}
                            {extra > 0 ? ` (+${extra})` : ""}
                          </Typography>
                        </Grid>
                        <Grid sx={{ width: 80 }} textAlign="center">
                          <VersionChip label={firstSm.model?.modelVersion || ""} />
                        </Grid>
                        <Grid sx={{ width: 100 }} textAlign="right">
                          <Typography color="text.secondary">{i18n.t("percent", { value: Number(scenarioModel?.percentAssigned || 0) })}</Typography>
                        </Grid>
                      </Grid>
                    );
                  }
                  // Fallback to active model if no checkboxes selected
                  if (selectionManager.selectedModel) {
                    return (
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid size="grow">
                          <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selectionManager.selectedModel.modelName}</Typography>
                        </Grid>
                        <Grid sx={{ width: 80 }} textAlign="center">
                          <VersionChip label={selectionManager.selectedModel.modelVersion || ""} />
                        </Grid>
                        <Grid sx={{ width: 100 }} textAlign="right">
                          <Typography color="text.secondary">
                            {i18n.t("percent", {
                              value: Number(
                                selectionManager.selectedScenario?.scenarioModels?.find((sm: ScenarioModel) => sm.modelId === selectionManager.selectedModel?.modelId)
                                  ?.percentAssigned || 0,
                              ),
                            })}
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  }
                  return <Typography>{i18n.t("assign-tab.models-select.title")}</Typography>;
                }}
              >
                <ListSubheader>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid sx={{ width: 40 }}>
                      <Checkbox
                        tabIndex={-1}
                        disableRipple
                        indeterminate={someSelected}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => toggleSelectAllModels(e.target.checked)}
                      />
                    </Grid>
                    <Grid size="grow">
                      <Box>{i18n.t("assign-tab.models-select.title")}</Box>
                    </Grid>
                    <Grid sx={{ width: 80 }} textAlign="center">
                      <Box color="text.secondary">{i18n.t("assign-tab.version.title")}</Box>
                    </Grid>
                    <Grid sx={{ width: 100 }} textAlign="right">
                      <Box color="text.secondary">{i18n.t("assign-tab.models-select.content")}</Box>
                    </Grid>
                  </Grid>
                </ListSubheader>
                {selectionManager.selectedScenario?.scenarioModels
                  ?.slice()
                  ?.sort((a: ScenarioModel, b: ScenarioModel) => {
                    // Sort by creation date descending (latest first)
                    const dateA = new Date(a.model?.createdAt || 0);
                    const dateB = new Date(b.model?.createdAt || 0);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .map((scenarioModel: ScenarioModel) => (
                      <MenuItem key={String(scenarioModel.modelId)} value={String(scenarioModel.modelId)}>
                        <Grid container alignItems="center" width="100%">
                          <Grid sx={{ width: 40 }}>
                            <Checkbox checked={!!(scenarioModel.modelId && selectedModelIds.includes(scenarioModel.modelId))} />
                          </Grid>
                          <Grid size="grow">
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "wrap", // Allow wrapping
                              }}
                            >
                              {scenarioModel.model?.modelName}
                            </Typography>
                          </Grid>
                          <Grid sx={{ width: 80 }} textAlign="center">
                            <VersionChip label={scenarioModel.model?.modelVersion || ""} />
                          </Grid>
                          <Grid sx={{ width: 100 }} textAlign="right">
                            <Typography color="text.secondary">{i18n.t("percent", { value: Number(scenarioModel.percentAssigned || 0) })}</Typography>
                          </Grid>
                        </Grid>
                      </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Stack>
      <Box mt={2} sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Box sx={{ position: "relative", flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {hasModelSelection ? (
            <MuiAssignGrid
              ref={assignGridRef}
              apiRef={assignGridApiRef}
              modelsDataService={modelsDataService}
              co2MaterialTypeLookup={co2MaterialTypeLookup}
              treeDataEnabled={enableTreeData}
              useMainThread={useMainThread}
              enableStringInterning={appStore.enableStringInterning}
              scenarioId={scenarioId}
              taskProgress={taskProgress}
              onSelectionChange={setHasGridSelection}
              isDataReady
              isCo2MaterialTypesLoading={isCo2MaterialTypesLoading}
              hasAssignedMaterialTypes={hasAssignedMaterialTypes}
              isDiscoveringAssignedMaterialTypes={isDiscoveringAssignedMaterialTypes}
            />
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
        </Box>
        <Grid my={1} container spacing={1} sx={{ justifyContent: "end" }}>
          <Grid>
            <Button
              disabled={!hasModelSelection || isBusy || !hasGridSelection || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
              loading={isAutoAssignMaterialsPending}
              variant="contained"
              onClick={handleAutoAssignMaterials}
            >
              {i18n.t("assign-tab.auto-assign-button.title")}
            </Button>
          </Grid>
          <Grid>
            <Button
              disabled={!hasModelSelection || isBusy || !hasGridSelection || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
              loading={isAssignMaterialsPending}
              variant="contained"
              onClick={handleAssignMaterialDlg}
            >
              {i18n.t("assign-tab.assign-button.title")}
            </Button>
          </Grid>
          {!appSettings.data?.profileLevelRebarSettings &&
            appSettings.data?.features?.includes(FeatureManagementType.RebarEstimation) &&
            (selectionManager.selectedScenario?.rebarEstimationStatus === RebarEstimationStatus.Enabled ||
              selectionManager.selectedScenario?.rebarEstimationStatus === RebarEstimationStatus.Estimated) && (
              <Grid>
                <Button
                  loading={isAssignRebarEstimationsPending}
                  variant="contained"
                  onClick={handleAssignRebarEstimations}
                  disabled={!hasModelSelection || isBusy || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
                >
                  {i18n.t("assign-tab.rebar-estimation-button.title")}
                </Button>
              </Grid>
            )}

          {appSettings.data?.profileLevelRebarSettings && selectionManager.selectedScenario?.profile?.rebarEstimationStatus === RebarEstimationStatus.Enabled && (
            <Grid>
              <Button
                loading={isAssignRebarEstimationsPending}
                variant="contained"
                onClick={handleAssignRebarEstimations}
                disabled={!hasModelSelection || isBusy || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
              >
                {i18n.t("assign-tab.rebar-estimation-button.title")}
              </Button>
            </Grid>
          )}

          {appSettings.data?.features?.includes(FeatureManagementType.NonModelledElements) &&
            (selectionManager.selectedScenario?.nonModelledElementsStatus === NonModelledElementsStatus.Enabled ||
              selectionManager.selectedScenario?.nonModelledElementsStatus === NonModelledElementsStatus.Estimated) && (
              <Grid>
                <Button
                  loading={isAssignNonModelledElementsPending}
                  variant="contained"
                  onClick={handleAssignNonModelledElements}
                  disabled={!hasModelSelection || isBusy || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
                >
                  {i18n.t("assign-tab.non-modelled-elements-button.title")}
                </Button>
              </Grid>
            )}
          <Grid>
            <Button
              loading={isRemoveMaterialsPending || isRemoveSupplementalsPending}
              disabled={isBusy || !hasGridSelection || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Delete)}
              variant="outlined"
              onClick={handleRemoveMaterials}
            >
              {i18n.t("assign-tab.remove-button.title")}
            </Button>
          </Grid>
          <Grid>
            <ToggleButtonGroup>
              <Tooltip title="Toggle Multi-Model Data Test Panel">
                <ToggleButton value="test" selected={showTestPanel} onChange={() => setShowTestPanel(!showTestPanel)} sx={{ fontSize: "0.875rem", padding: "7px" }}>
                  <BugReportIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={enableTreeData ? "Disable tree data (ALPHA)" : "Enable tree data (ALPHA)"}>
                <ToggleButton value="treeData" selected={enableTreeData} onChange={() => setEnableTreeData((prev) => !prev)} sx={{ fontSize: "0.875rem", padding: "7px" }}>
                  <AccountTreeIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={useMainThread ? "Using main thread for tree building (blocking UI)" : "Using web worker for tree building (non-blocking UI - default)"}>
                <ToggleButton value="mainThread" selected={useMainThread} onChange={() => setUseMainThread((prev) => !prev)} sx={{ fontSize: "0.875rem", padding: "7px" }}>
                  <MemoryIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={appStore.enableStringInterning ? "Using string interning for tree building (reduces memory usage)" : "Not using string interning for tree building (default)"}>
                <ToggleButton
                  value="stringInterning"
                  selected={appStore.enableStringInterning}
                  onChange={() => appStore.setEnableStringInterning(!appStore.enableStringInterning)}
                  sx={{ fontSize: "0.875rem", padding: "7px" }}
                >
                  <CompressIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        {openAssignMaterialDlg && (
          <MaterialSelectDialog
            ref={assignMaterialDlgRef}
            showMetrics
            showCompare={true}
            open={openAssignMaterialDlg}
            scenarioId={scenarioId}
            lcaoMService={lcaoMService}
            projectId={projectId}
            onClose={() => handleAssignMaterials()}
            onSubmit={(selectedCo2MaterialType: any) => handleAssignMaterials(selectedCo2MaterialType)}
          />
        )}
      </Box>
      <MultiModelDataTest showPanel={showTestPanel} onClose={() => setShowTestPanel(false)} />
    </Box>
  );
});

export default MuiAssignTab;
